import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

import { MIN_MS, SEC_MS } from '@dr.pogodin/js-utils';
import { Button } from '@dr.pogodin/react-utils';

import { Insight } from 'components/ai-app/Insight';

import type { UseExperienceResT } from 'hooks';
import type { UseAiBuildMetaResT } from 'hooks/useAiBuildMeta';

import { setExperienceTitle } from 'services/experience';

import {
  type ExperienceAndSessionMetaT,
  type InsightT,
  type VideoT,
  initExperienceData,
} from 'utils';

import whitePillButtonTheme from 'themes/Button/white-pill-h48-flat.scss';

import { BuildStages } from './BuildStages';
import { ClipsBrowser } from './ClipsBrowser';
import { EndButton } from './EndButton';
import { Experience } from './Experience';
import { GhostCard } from './GhostCard';

import { Talker } from './Talker';

import S from './style.scss';

const ANIMATION_DURATION = 2 * MIN_MS;

const REVEAL_DELAY_MS = 5 * SEC_MS;

const REVEAL_FADE_IN_MS = 450;
const REVEAL_FADE_OUT_MS = 1200;

type HeapT = {
  start: number;
};

type PropsT = {
  buildMeta: UseAiBuildMetaResT;
  editorMode: boolean;
  experience: UseExperienceResT;
  initLikeEditor?: boolean;
  insights: InsightT[];

  /** Called when the user clicks "Back to experiences" in the editor - takes
   *  them back to the start of a fresh check-in conversation. */
  onExit: () => void;

  onProgress: (progress: number) => void;
  ready: boolean;
  setEditorMode: (editorMode: boolean) => void;
};

export const ExperienceBuilder: FunctionComponent<PropsT> = ({
  buildMeta,
  editorMode,
  experience,
  initLikeEditor,
  insights,
  onExit,
  onProgress,
  ready,
  setEditorMode,
  // eslint-disable-next-line complexity
}) => {
  const heapRef = useRef<HeapT>({
    start: 0,
  });

  const [stage, setStage] = useState(
    initLikeEditor ? Number.MAX_SAFE_INTEGER : 0,
  );

  const experienceData = experience.data?.experience;
  const hasVideos = !!experienceData?.videos.length;

  const [showClipsBrowser, setShowClipsBrowser] = useState(
    editorMode && !hasVideos,
  );

  if (editorMode && !hasVideos && !showClipsBrowser) {
    setShowClipsBrowser(true);
  }

  const numStages = 1 + (buildMeta?.steps.length ?? 0);

  if (numStages > 1 && stage === 0) setStage(1);

  const [revealEditor, setRevealEditor] = useState(initLikeEditor ?? false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayOpaque, setOverlayOpaque] = useState(false);
  const [talkerReady, setTalkerReady] = useState(false);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => {
    clearTimeout(revealTimerRef.current);
  }, []);

  const onTalkerReady = () => {
    setTalkerReady(true);
    if (revealEditor || revealTimerRef.current) return;
    revealTimerRef.current = setTimeout(() => {
      setOverlayVisible(true);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setOverlayOpaque(true);
        });
      });
    }, REVEAL_DELAY_MS);
  };

  const sessionNumber = experienceData?.videos.length ?? 0;

  const titleSyncedForIdRef = useRef<string>(undefined);

  const fallbackTitle = buildMeta?.title;
  useEffect(() => {
    if (
      experienceData?.id
      && !experienceData.title
      && fallbackTitle
      && titleSyncedForIdRef.current !== experienceData.id
    ) {
      titleSyncedForIdRef.current = experienceData.id;
      void setExperienceTitle(experienceData.id, fallbackTitle).then(() => {
        experience.set({
          checkInId: null,
          sessionKind: null,
          ...experience.data,
          experience: { ...experienceData, title: fallbackTitle },
        });
      });
    }
  }, [experience, experienceData, fallbackTitle]);

  const progressEvent = useEffectEvent(onProgress);

  useEffect(() => {
    const heap = heapRef.current;
    heap.start ||= Date.now();

    const id = setInterval(() => {
      const now = Date.now();
      let progress = Math.min((now - heap.start) / ANIMATION_DURATION, 1);

      let nextStage = Math.floor(numStages * progress);
      if (numStages > 1 && nextStage === 0) nextStage = 1;

      if (ready) {
        nextStage = Number.MAX_SAFE_INTEGER;
        progress = 1;
      } else {
        if (nextStage > numStages - 1) nextStage = Math.max(0, numStages - 2);
        if (progress > 0.9) progress = 0.9;
      }

      setStage(nextStage);
      progressEvent(progress);
    }, SEC_MS);
    return () => {
      clearInterval(id);
    };
  }, [numStages, ready]);

  const endButton = stage >= numStages ? (
    <EndButton
      editMode={editorMode}
      experience={experience}
      green={editorMode}
      setEditMode={setEditorMode}
    />
  ) : null;

  const insightNodes: ReactNode[] = [];
  for (let i = 0; i < insights.length; ++i) {
    const insight = insights[i];
    if (!insight) throw Error('Internal error');
    insightNodes.push(
      <Insight
        category={insight.category}
        key={`${i}/${insight.insight}`}
      >
        {insight.insight}
      </Insight>,
    );
  }

  let rightPanelContent: ReactNode;

  const videosRef = useRef<HTMLDivElement>(null);

  const addVideo = (video: VideoT, index: number | undefined) => {
    void experience.reload((old): ExperienceAndSessionMetaT => {
      const videos = old?.experience.videos.slice() ?? [];
      const append = index === undefined || index >= videos.length;
      if (append) videos.push(video);
      else videos.splice(index, 0, video);

      // TODO: Hacky... make the scrolling better, without relying
      // on the timeout.
      if (append) {
        setTimeout(() => {
          videosRef.current?.scrollTo(
            0,
            videosRef.current.scrollHeight,
          );
        }, 300);
      }

      return {
        checkInId: old?.checkInId ?? null,
        experience: {
          ...initExperienceData(),
          ...old,

          // NOTE: Here, and in the following functions, removeVideo(),
          // and clearVideo(), re-setting `id` to "undefined" is our way
          // to mark the experience as edited by user, so that it is saved
          // as a new, user-edited experience, at the start of the session
          // (which currently happens inside the <LaunchExperienceButton>
          // component logic).
          id: undefined,

          isAvailable: videos.every((item) => item.status === 'Published'),
          videos,
        },
        sessionKind: old?.sessionKind ?? null,
      };
    });
  };

  const removeVideo = (idx: number) => {
    void experience.reload((old): ExperienceAndSessionMetaT => {
      const videos = old?.experience.videos.slice() ?? [];
      videos.splice(idx, 1);
      return {
        checkInId: old?.checkInId ?? null,
        experience: {
          ...initExperienceData(),
          ...old,
          id: undefined,
          isAvailable: videos.every((item) => item.status === 'Published'),
          videos,
        },
        sessionKind: old?.sessionKind ?? null,
      };
    });
  };

  const clearVideos = () => {
    void experience.reload((old): ExperienceAndSessionMetaT => ({
      checkInId: old?.checkInId ?? null,
      experience: {
        ...initExperienceData(),
        ...old,
        id: undefined,
        isAvailable: true,
        videos: [],
      },
      sessionKind: old?.sessionKind ?? null,
    }));
  };

  if (revealEditor) {
    rightPanelContent = (
      <Experience
        addVideo={addVideo}
        editMode={editorMode}
        experience={experience}
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        name={experienceData?.title || buildMeta?.title}
        onBack={onExit}
        removeVideo={removeVideo}
        sessionNumber={sessionNumber}
        setEditMode={() => {
          setShowClipsBrowser((prev) => !prev);
        }}
        showClipsBrowser={showClipsBrowser}
        videosRef={videosRef}
      />
    );
  } else {
    rightPanelContent = (
      <div styleName="ghostCards">
        {/* TODO: Maybe make these numbers dependent on the numStages? */}
        {stage >= 2 ? <GhostCard /> : null}
        {stage >= 3 ? <GhostCard /> : null}
        {stage >= 4 ? <GhostCard /> : null}
      </div>
    );
  }

  // TODO: This does hide the left panel visually, but does not really
  // unmount it from DOM, which we should do, if this UI is kept.
  let leftClassName = S.leftColumn;
  let experienceViewerClassName = S.centralAndRightColumns;
  if (editorMode) {
    leftClassName += ` ${S.exit}`;
  }
  if (editorMode && showClipsBrowser) {
    experienceViewerClassName += ` ${S.editMode}`;
  }

  // let totalDurationMins = 0;
  // if (experience.data?.videos.length) {
  //   for (const video of experience.data.videos) {
  //     totalDurationMins += video.durationMin ?? 0;
  //   }
  // }

  let containerClassName = S.container;
  if (revealEditor) {
    containerClassName += ` ${S.ready}`;
  }
  if (!editorMode) {
    containerClassName += ` ${S.building}`;
  }

  return (
    <div className={containerClassName}>
      <div className={leftClassName}>
        <div styleName="left">
          {/* {stage > 1 && stage < numStages ? (
            <div styleName="insights">
              {insightNodes}
            </div>
          ) : null} */}
        </div>
      </div>
      <div className={experienceViewerClassName}>
        {revealEditor ? null : (
          <>
            <Talker
              experienceIsReady={stage >= numStages}
              onReady={onTalkerReady}
            />
            {talkerReady ? null : (
              <BuildStages
                current={stage}
                stages={buildMeta?.steps ?? []}
              />
            )}
          </>
        )}
        <div styleName="rightColumn">
          {rightPanelContent}
          {endButton && editorMode ? (
            <div styleName="editFooter">
              <div styleName="clearButtonWrap">
                <Button onClick={clearVideos} theme={whitePillButtonTheme}>
                  Clear
                </Button>
              </div>
              {endButton}
            </div>
          ) : null}
          {/* {endButton && !editorMode ? (
            <>
              <div styleName="endCard">
                {experience.data?.videos.length ? (
                  <div styleName="endSummary">
                    {`Your ${Math.ceil(totalDurationMins)}-minute flow is ready — ${experience.data.videos.length} session${experience.data.videos.length > 1 ? 's' : ''} personalized for ${
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      buildMeta?.title || 'your wellbeing.'
                    }`}
                  </div>
                ) : null}
              </div>
              {endButton}
            </>
          ) : null} */}
        </div>
      </div>
      {editorMode && showClipsBrowser ? (
        <ClipsBrowser
          addVideoToEnd={(video) => {
            addVideo(video, undefined);
          }}
        />
      ) : null}
      {overlayVisible ? (
        <div
          onTransitionEnd={() => {
            if (overlayOpaque) {
              setEditorMode(true);
              setRevealEditor(true);
              setOverlayOpaque(false);
            } else {
              setOverlayVisible(false);
            }
          }}
          style={{
            opacity: overlayOpaque ? 1 : 0,
            transitionDuration: `${overlayOpaque ? REVEAL_FADE_IN_MS : REVEAL_FADE_OUT_MS}ms`,
          }}
          styleName="revealOverlay"
        />
      ) : null}
    </div>
  );
};
